import { useEffect, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { fl } from "@/lib/finI18n";
import { fmtDate } from "@/components/DateInput";
import { Loader2 } from "lucide-react";

const rateFmt = (v) => Number(v || 0).toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 4 });

function TrendCard({ cur, hist, lang }) {
  const pts = hist.filter((w) => w.rates?.[cur] != null).map((w) => ({ name: fmtDate(w.start).slice(0, 5), rate: w.rates[cur], period: `${fmtDate(w.start)}–${fmtDate(w.end)} · KMK ${w.kmk_no}` }));
  if (!pts.length) return null;
  const first = pts[0].rate; const last = pts[pts.length - 1].rate;
  const diff = last - first; const p = first ? (diff / first) * 100 : 0;
  const tone = diff > 0 ? "text-emerald-700" : diff < 0 ? "text-rose-600" : "text-muted-foreground";
  const color = diff > 0 ? "#059669" : diff < 0 ? "#e11d48" : "#64748b";
  return (
    <div className="rounded-md border border-border p-3" data-testid={`kmk-trend-${cur}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="num font-semibold">{cur}</span>
        <span className="num text-sm">Rp {rateFmt(last)}</span>
      </div>
      <p className={`num text-[11px] ${tone}`} data-testid={`kmk-trend-change-${cur}`}>
        {diff > 0 ? "▲ +" : diff < 0 ? "▼ −" : "■ "}{rateFmt(Math.abs(diff))} ({diff < 0 ? "−" : "+"}{Math.abs(p).toLocaleString("id-ID", { maximumFractionDigits: 2 })}%) {fl("kmk12w", lang)}
      </p>
      <div className="h-32 mt-1">
        <ResponsiveContainer>
          <LineChart data={pts} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} width={52} domain={["auto", "auto"]} tickFormatter={(v) => Number(v).toLocaleString("id-ID", { maximumFractionDigits: 2 })} />
            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v) => [`Rp ${rateFmt(v)}`, cur]} labelFormatter={(_, pl) => pl?.[0]?.payload?.period || ""} />
            <Line type="monotone" dataKey="rate" stroke={color} strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function KmkTrend({ currencies, lang }) {
  const [hist, setHist] = useState(null);
  useEffect(() => { api.get("/kurs/kmk/history?weeks=12").then(({ data }) => setHist(data)).catch(() => setHist([])); }, []);
  const curs = [...new Set(["USD", ...currencies])];
  return (
    <div className="space-y-2" data-testid="kmk-trend">
      <p className="text-sm font-semibold">{fl("kmkTrend", lang)}{hist?.length ? <span className="ml-2 text-[11px] font-normal text-muted-foreground" data-testid="kmk-trend-weeks">{fmtDate(hist[0].start)} – {fmtDate(hist[hist.length - 1].end)} · {hist.length} KMK</span> : null}</p>
      {!hist && <div className="py-6 text-center text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /></div>}
      {hist && <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{curs.map((c) => <TrendCard key={c} cur={c} hist={hist} lang={lang} />)}</div>}
    </div>
  );
}
